/* eslint-disable @typescript-eslint/no-misused-promises */
import {Button, Dialog, Intent} from "@blueprintjs/core";
import React, {forwardRef, useCallback, useContext, useImperativeHandle, useMemo, useState, useRef} from "react";
import {AuthContext} from "../../../providers/AuthProvider";
import styled from "@emotion/styled";
import TemporaryPasswordDialog, {TemporaryPasswordDialogRef} from "./TemporaryPasswordDialog";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {UserProfile} from "./UserProfile";
import {User, defaultGroups} from "./UserManagement";
import {createUser, generateTemporaryPassword} from "../../../services/TenantService";
import UserForm from "./UserForm";
import {uniqueId} from "lodash";
import {AppToaster} from "../../../utils/toasters/toaster";
import {useOktaAuth} from "@okta/okta-react";
import log from "loglevel";
import {useFeatureFlagContext} from "../../../providers/FeatureFlagProvider";
import {Constants, UserPreferences} from "../../../constants/constants";
import {useSetUserPrefMutation} from "../../../generated/graphql";
import {createJsonPref, extractSimplePref, usePreferenceContext} from "../../../providers/PreferenceProvider";

interface InviteUserDialogProps {
  refreshData: () => void;
}

export interface InviteUserDialogRef {
  onOpen: () => void;
  onClose: () => void;
  onOpenUpdate: (userToEdit: User) => void;
}

export enum FormMode {
  new,
  edit
}

enum SuccessCodeStatus {
  OK = 200,
  CREATED = 201
}

type UserEditorState = {
  formId: string;
  formData: UserProfile;
  emailValid: boolean;
  emailAlreadyExists: boolean;
  deletedUserRestorable: boolean;
  firstNameValid: boolean;
  lastNameValid: boolean;
  legacyUserIdValid: boolean;
  formValid: boolean;
};

type FormStatus = {
  formId: string;
  status: "success" | "failed";
  submittedEmail: string;
  errorMessage?: string;
  restorable?: boolean;
  restoreFailed?: boolean;
  restored?: boolean;
};

const formDataOf = (userForms: UserEditorState[], statuses: FormStatus[]): UserProfile[] =>
  userForms.filter((form) => statuses.some((item) => item.formId === form.formId)).map((form) => form.formData);

const produceSuccessMessage = (formMode: FormMode, userValues: UserProfile[]): string => {
  const emailsString = userValues.map((item) => item.email).join(", ");

  return formMode === FormMode.new
    ? `Invited User(s): ${emailsString} to Dispatch! An email has been sent in order to activate their account.`
    : `User: ${emailsString} updated!`;
};

const isAlreadyExistsError = (errorMessage?: string): boolean =>
  !!errorMessage?.toLowerCase().includes("already exists");

// Must match CreateUserHandler.DELETED_USER_RESTORABLE / RESTORE_FAILED (nxtgen-services). A restorable
// response's `message` also says "already exists", so check codes before isAlreadyExistsError.
const DELETED_USER_RESTORABLE = "DELETED_USER_RESTORABLE";
const RESTORE_FAILED = "RESTORE_FAILED";

const responseCode = (data: unknown): string | undefined => (data as {code?: string} | undefined)?.code;

// A request-only flag, so it stays off UserProfile (whose keys are the form fields).
const withRestoreDeletedUser = (formData: UserProfile): UserProfile =>
  Object.assign({...formData}, {restoreDeletedUser: true});

// Every restore re-activates the account through Okta, so an activation email always goes out.
const produceRestoreMessage = (userValues: UserProfile[]): string =>
  `Restored User(s): ${userValues
    .map((item) => item.email)
    .join(", ")} to Dispatch. An email has been sent in order to activate their account.`;

const produceSubmitSuccessMessage = (
  formMode: FormMode,
  userForms: UserEditorState[],
  successStatuses: FormStatus[]
): string => {
  const created = successStatuses.filter((item) => !item.restored);
  const restored = successStatuses.filter((item) => item.restored);
  return [
    created.length > 0 ? produceSuccessMessage(formMode, formDataOf(userForms, created)) : "",
    restored.length > 0 ? produceRestoreMessage(formDataOf(userForms, restored)) : ""
  ]
    .filter(Boolean)
    .join(" ");
};

// Count-neutral on purpose: it reads the same for one email or several.
const produceRestorableMessage = (userValues: UserProfile[], tenantName: string): string =>
  `Previously removed from Dispatch: ${userValues
    .map((item) => item.email)
    .join(", ")}. Click Resend Invitation to restore access to ${tenantName}.`;

const produceRestoreFailureMessage = (userValues: UserProfile[]): string =>
  `Could not restore user(s): ${userValues.map((item) => item.email).join(", ")}. Please try again.`;

const produceFailureMessage = (formMode: FormMode, userValues: UserProfile[]): string => {
  const legacyUserIdString = Array.from(new Set(userValues.map((item) => item.legacyUserId))).join(", ");
  const prefix = `Failed to ${formMode === FormMode.new ? "invite" : "update"} user(s):`;
  const emailsString = userValues.map((item) => item.email).join(", ");
  return `${prefix} ${emailsString}. Please make sure the e-Courier Username: ${legacyUserIdString}, exists in CMS.`;
};

const validateForm = (formState: UserEditorState, fieldName: keyof UserProfile) => {
  const value = formState.formData[fieldName]!;
  switch (fieldName) {
    case "email":
      formState.emailValid = Constants.REGEX.EMAIL_REGEX.test(value as string);
      formState.emailAlreadyExists = false;
      formState.deletedUserRestorable = false;
      break;
    case "firstName":
      formState.firstNameValid = value.length > 0;
      break;
    case "lastName":
      formState.lastNameValid = value.length > 0;
      break;
    case "legacyUserId":
      formState.legacyUserIdValid = value.length > 0;
      break;
    default:
      break;
  }
  formState.formValid =
    formState.emailValid &&
    !formState.emailAlreadyExists &&
    formState.firstNameValid &&
    formState.lastNameValid &&
    formState.legacyUserIdValid;
};

const MAXIMUM_INVITED_USERS = 10;

const InviteUserDialog = forwardRef<InviteUserDialogRef, InviteUserDialogProps>(({refreshData}, ref) => {
  const {tempPassword: temporaryPasswordEnabled} = useFeatureFlagContext();
  const [isOpen, setIsOpen] = useState(false);
  const authContext = useContext(AuthContext);
  const {userPreferences, userPrefsQueryRefetch} = usePreferenceContext();
  const [setUserPref] = useSetUserPrefMutation();
  const cmsHintDismissed =
    extractSimplePref(userPreferences, UserPreferences.hideCmsUsernameHint, false).value === true;
  const handleDismissCmsHint = useCallback(async () => {
    await setUserPref({variables: {name: UserPreferences.hideCmsUsernameHint, input: createJsonPref(true)}})
      .then(() => {
        userPrefsQueryRefetch?.();
      })
      .catch((error: Error) => {
        log.error(error);
      });
  }, [setUserPref, userPrefsQueryRefetch]);

  const newUserState = useCallback((): UserProfile => {
    return new UserProfile(
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      authContext.tenantId as string,
      defaultGroups(authContext),
      createUser,
      generateTemporaryPassword
    );
  }, [authContext]);

  const createUserForm = useCallback((): UserEditorState => {
    return {
      formId: uniqueId(),
      formData: newUserState(),
      emailValid: false,
      emailAlreadyExists: false,
      deletedUserRestorable: false,
      firstNameValid: false,
      lastNameValid: false,
      legacyUserIdValid: false,
      formValid: false
    };
  }, [newUserState]);

  const [beforeUpdateUserValues, setBeforeUpdateUserValues] = useState<UserProfile>();
  const [userForms, setUserForms] = useState<UserEditorState[]>([createUserForm()]);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [isGeneratingTempPassword, setIsGeneratingTempPassword] = useState<boolean>(false);
  const tempPasswordDialogRef = useRef<TemporaryPasswordDialogRef>(null);
  const [formMode, setFormMode] = useState<FormMode>(FormMode.new);
  const [showResendButton, setShowResendButton] = useState(false);
  const {oktaAuth} = useOktaAuth();

  const handleUserFormChange = (formId: string, fieldName: keyof UserProfile, value: any) => {
    const editingFormIndex = userForms.findIndex((item) => item.formId === formId);
    let editingFormState = {...userForms[editingFormIndex]};
    editingFormState = {...editingFormState, formData: {...editingFormState.formData, [fieldName]: value}};
    validateForm(editingFormState, fieldName);
    const newUserEditorFormState = [...userForms];
    newUserEditorFormState[editingFormIndex] = editingFormState;
    setUserForms(newUserEditorFormState);
  };

  const resetForm = useCallback(() => {
    setFormMode(FormMode.new);
    setUserForms([createUserForm()]);
  }, [createUserForm]);

  const onOpen = useCallback(() => {
    setIsOpen(true);
    resetForm();
  }, [resetForm]);

  const onClose = useCallback(() => {
    setShowResendButton(false);
    setIsOpen(false);
    resetForm();
  }, [resetForm]);

  const onOpenUpdate = useCallback((userToEdit: User) => {
    setIsOpen(true);
    const userProfile = userToEdit.profile;
    setBeforeUpdateUserValues(userProfile);
    setFormMode(FormMode.edit);
    setUserForms([
      {
        formId: uniqueId(),
        formData: userProfile,
        emailValid: true,
        emailAlreadyExists: false,
        deletedUserRestorable: false,
        firstNameValid: true,
        lastNameValid: true,
        legacyUserIdValid: true,
        formValid: true
      }
    ]);
  }, []);

  const handleAddNewForm = useCallback(() => {
    setShowResendButton(false);
    setUserForms((prev) => [...prev, createUserForm()]);
  }, [createUserForm]);

  const handleRemoveForm = useCallback((formId: string) => {
    setUserForms((prev) => prev.filter((item) => item.formId != formId));
  }, []);

  useImperativeHandle(ref, () => ({
    onClose,
    onOpen,
    onOpenUpdate
  }));

  const handleUserEditorSubmit = async () => {
    setIsUpdating(true);
    const invitedFormsStatus: FormStatus[] = [];
    await Promise.all(
      userForms.map((item) =>
        item.formData
          .persistHandler(
            oktaAuth.getAccessToken() ?? "",
            item.deletedUserRestorable ? withRestoreDeletedUser(item.formData) : item.formData
          )
          .then((r) => {
            const isSuccess = r.status === SuccessCodeStatus.OK || r.status === SuccessCodeStatus.CREATED;
            invitedFormsStatus.push({
              formId: item.formId,
              submittedEmail: item.formData.email,
              status: isSuccess ? "success" : "failed",
              // Restore answers 200; a 201 means the account vanished between submits and was created anew.
              restored: item.deletedUserRestorable && r.status === SuccessCodeStatus.OK
            });
          })
          .catch((e: any) => {
            invitedFormsStatus.push({
              formId: item.formId,
              submittedEmail: item.formData.email,
              status: "failed",
              errorMessage: typeof e.response?.data === "string" ? e.response.data : JSON.stringify(e.response?.data),
              restorable: responseCode(e.response?.data) === DELETED_USER_RESTORABLE,
              restoreFailed: responseCode(e.response?.data) === RESTORE_FAILED
            });
            log.error(e);
          })
      )
    );
    const successStatuses = invitedFormsStatus.filter((item) => item.status === "success");
    if (successStatuses.length === invitedFormsStatus.length) {
      AppToaster.show({
        intent: Intent.SUCCESS,
        icon: "tick",
        message: produceSubmitSuccessMessage(formMode, userForms, successStatuses)
      });
      refreshData();
      onClose();
    } else {
      const successFormIds = successStatuses.map((item) => item.formId);
      const failedStatuses = invitedFormsStatus.filter((item) => item.status === "failed");
      // A failed restore was rolled back to a deleted account, so the notice stays and the admin can retry.
      const restorableStatuses = failedStatuses.filter((item) => item.restorable);
      const restoreFailedStatuses = failedStatuses.filter((item) => item.restoreFailed);
      const restorableFormIds = failedStatuses
        .filter((item) => item.restorable || item.restoreFailed)
        .map((item) => item.formId);
      const unrestorableStatuses = failedStatuses.filter((item) => !item.restorable && !item.restoreFailed);
      const alreadyExistsFormIds = unrestorableStatuses
        .filter((item) => isAlreadyExistsError(item.errorMessage))
        .map((item) => item.formId);
      const otherFailedStatuses = unrestorableStatuses.filter((item) => !isAlreadyExistsError(item.errorMessage));
      if (successFormIds.length > 0) {
        AppToaster.show({
          intent: Intent.SUCCESS,
          icon: "tick",
          message: produceSubmitSuccessMessage(formMode, userForms, successStatuses)
        });
      }
      if (restorableStatuses.length > 0) {
        // The per-form notice can sit below the fold with many forms; this summarises the same instruction.
        AppToaster.show({
          intent: Intent.PRIMARY,
          icon: "info-sign",
          message: produceRestorableMessage(
            formDataOf(userForms, restorableStatuses),
            authContext.tenant?.shortName ?? ""
          )
        });
      }
      if (restoreFailedStatuses.length > 0) {
        AppToaster.show({
          intent: Intent.WARNING,
          icon: "warning-sign",
          message: produceRestoreFailureMessage(formDataOf(userForms, restoreFailedStatuses))
        });
      }
      if (otherFailedStatuses.length > 0) {
        AppToaster.show({
          intent: Intent.WARNING,
          icon: "warning-sign",
          message: produceFailureMessage(formMode, formDataOf(userForms, otherFailedStatuses))
        });
      }
      if (formMode === FormMode.new) {
        setShowResendButton(true);
      }
      setUserForms((prev) =>
        prev
          .filter((item) => !successFormIds.includes(item.formId))
          .map((item) => {
            // A response describes the email that was submitted. If it was edited while the request was in
            // flight (or the form was added meanwhile), the answer no longer applies to this form.
            const status = invitedFormsStatus.find((s) => s.formId === item.formId);
            if (status?.submittedEmail !== item.formData.email) {
              return item;
            }
            // Latest response wins: a notice from an earlier submit must not survive a non-restorable answer.
            const form = {...item, deletedUserRestorable: restorableFormIds.includes(item.formId)};
            return alreadyExistsFormIds.includes(item.formId)
              ? {...form, emailAlreadyExists: true, formValid: false}
              : form;
          })
      );
    }
    setIsUpdating(false);
  };

  const handleGenerateTempPassword = async () => {
    setIsGeneratingTempPassword(true);
    try {
      const response = await userForms[0].formData.generateTemporaryPassword!(
        oktaAuth.getAccessToken() ?? "",
        userForms[0].formData.id
      );
      const tempPassword = response.data.temporaryPassword;
      tempPasswordDialogRef.current?.open(tempPassword);
    } catch (error) {
      log.error(error);
      AppToaster.show({
        intent: Intent.DANGER,
        icon: "error",
        message: `Failed to generate temporary password for ${beforeUpdateUserValues?.email}. Please try again later.`
      });
    } finally {
      setIsGeneratingTempPassword(false);
    }
  };

  const isFormDirtyAndValid = useMemo(() => {
    if (formMode === FormMode.edit) {
      return userForms[0].formValid && JSON.stringify(beforeUpdateUserValues) !== JSON.stringify(userForms[0].formData);
    } else {
      return userForms.every((item) => item.formValid);
    }
  }, [beforeUpdateUserValues, formMode, userForms]);

  return (
    <StyledDialog
      isOpen={isOpen}
      title={
        formMode === FormMode.new
          ? "Invite User to Dispatch"
          : `Edit ${beforeUpdateUserValues?.firstName} ${beforeUpdateUserValues?.lastName}`
      }
      onClose={onClose}
      icon={<FontAwesomeIcon style={{paddingRight: "10px"}} icon={solid("user-plus")} />}
      canEscapeKeyClose
      isCloseButtonShown
    >
      <DialogBody data-testid="create-user-dialog">
        <CustomerName>Customer: {`${authContext.tenant?.shortName}`}</CustomerName>
        <FormList>
          {userForms.map((form) => (
            <UserForm
              key={form.formId}
              formMode={formMode}
              formId={form.formId}
              formData={form.formData}
              emailValid={form.emailValid}
              emailAlreadyExists={form.emailAlreadyExists}
              deletedUserRestorable={form.deletedUserRestorable}
              cmsHintDismissed={cmsHintDismissed}
              onDismissCmsHint={handleDismissCmsHint}
              onFormChange={handleUserFormChange}
              onRemoveForm={userForms.length > 1 ? handleRemoveForm : undefined}
            />
          ))}
        </FormList>
        {formMode === FormMode.new && (
          <AddNewButton
            data-testid="add-new-user-form"
            disabled={userForms.length >= MAXIMUM_INVITED_USERS}
            variant="minimal"
            icon="plus"
            onClick={handleAddNewForm}
          >
            Add new user
          </AddNewButton>
        )}
      </DialogBody>
      <DialogFooter>
        <Button
          loading={isUpdating}
          data-testid={"create-user-submit-btn"}
          disabled={!isFormDirtyAndValid || isUpdating}
          text={
            // The restorable notice names this button, so keep the label even after "Add new user" resets it.
            showResendButton || userForms.some((form) => form.deletedUserRestorable)
              ? "Resend Invitation"
              : formMode === FormMode.new
              ? "Send Invitation"
              : "Update User"
          }
          onClick={handleUserEditorSubmit}
          intent={Intent.PRIMARY}
        />
        {temporaryPasswordEnabled && formMode === FormMode.edit && !authContext.isEcourierUser && (
          <Button
            loading={isGeneratingTempPassword}
            disabled={isGeneratingTempPassword}
            data-testid={"generate-temp-password-btn"}
            text={"Generate Temporary Password"}
            onClick={handleGenerateTempPassword}
          />
        )}
      </DialogFooter>
      <TemporaryPasswordDialog ref={tempPasswordDialogRef} />
    </StyledDialog>
  );
});

InviteUserDialog.displayName = "InviteUserDialog";

export default InviteUserDialog;

const StyledDialog = styled(Dialog)`
  border-radius: 12px;
  overflow: hidden;
  height: 600px;
  background: #fff;
  display: flex;
  font-family: Roboto;
`;

const CustomerName = styled.div`
  font-weight: 500;
  font-size: 16px;
`;

const DialogBody = styled.div`
  padding: 10px 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex-grow: 1;
  overflow: hidden;
`;

const FormList = styled.div`
  overflow-y: auto;
`;

const DialogFooter = styled.div`
  display: flex;
  flex-direction: row-reverse;
  justify-content: space-between;
  padding: 0 50px 24px;

  .bp5-button {
    border-radius: 6px;
  }
`;

const AddNewButton = styled(Button)`
  width: fit-content;
`;
